import type { ReactNode } from 'react';

/**
 * Hand-authored duotone furniture icons (brand teal). Each entry returns the
 * inner SVG shapes for a 0 0 48 48 viewBox; `FurnitureIcon` provides the
 * wrapper. Two tones via Tailwind fill utilities: `fill-petrol` (strong
 * silhouette) + `fill-petrol-soft` (light insets).
 */
const S = 'fill-petrol';
const L = 'fill-petrol-soft';

export const FURNITURE_ICONS: Record<string, () => ReactNode> = {
  sofa: () => (
    <>
      <rect x="9" y="15" width="30" height="14" rx="5" className={L} />
      <rect x="6" y="26" width="36" height="9" rx="3" className={S} />
      <rect x="6" y="22" width="8" height="13" rx="3" className={S} />
      <rect x="34" y="22" width="8" height="13" rx="3" className={S} />
      <rect x="9" y="35" width="3" height="5" className={S} />
      <rect x="36" y="35" width="3" height="5" className={S} />
    </>
  ),
  armchair: () => (
    <>
      <rect x="15" y="15" width="18" height="14" rx="5" className={L} />
      <rect x="13" y="26" width="22" height="9" rx="3" className={S} />
      <rect x="11" y="22" width="7" height="13" rx="3" className={S} />
      <rect x="30" y="22" width="7" height="13" rx="3" className={S} />
      <rect x="14" y="35" width="3" height="5" className={S} />
      <rect x="31" y="35" width="3" height="5" className={S} />
    </>
  ),
  coffee_table: () => (
    <>
      <rect x="6" y="20" width="36" height="6" rx="3" className={S} />
      <rect x="9" y="26" width="3" height="12" className={L} />
      <rect x="36" y="26" width="3" height="12" className={L} />
      <rect x="18" y="26" width="3" height="12" className={L} />
      <rect x="27" y="26" width="3" height="12" className={L} />
    </>
  ),
  tv: () => (
    <>
      <rect x="8" y="12" width="32" height="22" rx="3" className={S} />
      <rect x="11" y="15" width="26" height="16" rx="2" className={L} />
      <rect x="20" y="34" width="8" height="3" className={S} />
      <rect x="15" y="37" width="18" height="3" rx="1.5" className={S} />
    </>
  ),
  tv_stand: () => (
    <>
      <rect x="6" y="22" width="36" height="14" rx="3" className={S} />
      <rect x="9" y="25" width="13" height="8" rx="1.5" className={L} />
      <rect x="26" y="25" width="13" height="8" rx="1.5" className={L} />
      <rect x="10" y="36" width="3" height="4" className={S} />
      <rect x="35" y="36" width="3" height="4" className={S} />
    </>
  ),
  bookshelf: () => (
    <>
      <rect x="12" y="8" width="24" height="32" rx="3" className={S} />
      <rect x="15" y="12" width="18" height="6" rx="1" className={L} />
      <rect x="15" y="21" width="18" height="6" rx="1" className={L} />
      <rect x="15" y="30" width="18" height="6" rx="1" className={L} />
    </>
  ),
  floor_lamp: () => (
    <>
      <path d="M16 16 H32 L28 8 H20 Z" className={L} />
      <rect x="23" y="16" width="2" height="20" className={S} />
      <rect x="17" y="36" width="14" height="3" rx="1.5" className={S} />
    </>
  ),
  rug: () => (
    <>
      <ellipse cx="24" cy="26" rx="18" ry="10" className={S} />
      <ellipse cx="24" cy="26" rx="13" ry="6.5" className={L} />
    </>
  ),
  plant: () => (
    <>
      <circle cx="24" cy="16" r="9" className={L} />
      <circle cx="17" cy="20" r="6" className={L} />
      <circle cx="31" cy="20" r="6" className={L} />
      <path d="M16 28 H32 L29 40 H19 Z" className={S} />
    </>
  ),
  bed: () => (
    <>
      <rect x="6" y="16" width="6" height="19" rx="2" className={S} />
      <rect x="6" y="26" width="36" height="9" rx="3" className={S} />
      <rect x="14" y="21" width="13" height="8" rx="2" className={L} />
      <rect x="8" y="35" width="3" height="4" className={S} />
      <rect x="37" y="35" width="3" height="4" className={S} />
    </>
  ),
  wardrobe: () => (
    <>
      <rect x="12" y="8" width="24" height="32" rx="3" className={S} />
      <rect x="23" y="10" width="2" height="28" className={L} />
      <rect x="19" y="21" width="2" height="7" rx="1" className={L} />
      <rect x="27" y="21" width="2" height="7" rx="1" className={L} />
    </>
  ),
  dresser: () => (
    <>
      <rect x="8" y="14" width="32" height="24" rx="3" className={S} />
      <rect x="11" y="17" width="26" height="5" rx="1" className={L} />
      <rect x="11" y="24" width="26" height="5" rx="1" className={L} />
      <rect x="11" y="31" width="26" height="4" rx="1" className={L} />
      <rect x="9" y="38" width="3" height="3" className={S} />
      <rect x="36" y="38" width="3" height="3" className={S} />
    </>
  ),
  nightstand: () => (
    <>
      <rect x="14" y="16" width="20" height="18" rx="3" className={S} />
      <rect x="17" y="19" width="14" height="5" rx="1" className={L} />
      <rect x="17" y="26" width="14" height="5" rx="1" className={L} />
      <rect x="16" y="34" width="3" height="5" className={S} />
      <rect x="29" y="34" width="3" height="5" className={S} />
    </>
  ),
  mirror: () => (
    <>
      <rect x="16" y="8" width="16" height="32" rx="8" className={S} />
      <rect x="19" y="11" width="10" height="26" rx="5" className={L} />
    </>
  ),
  dining_table: () => (
    <>
      <rect x="6" y="18" width="36" height="5" rx="2" className={S} />
      <rect x="9" y="23" width="3" height="16" className={L} />
      <rect x="36" y="23" width="3" height="16" className={L} />
      <rect x="18" y="23" width="3" height="16" className={L} />
      <rect x="27" y="23" width="3" height="16" className={L} />
    </>
  ),
  chair: () => (
    <>
      <rect x="16" y="10" width="16" height="5" rx="2" className={L} />
      <rect x="16" y="11" width="3" height="13" className={S} />
      <rect x="14" y="22" width="20" height="5" rx="2" className={S} />
      <rect x="16" y="27" width="3" height="12" className={S} />
      <rect x="29" y="27" width="3" height="12" className={S} />
    </>
  ),
  fridge: () => (
    <>
      <rect x="14" y="8" width="20" height="32" rx="3" className={S} />
      <rect x="14" y="20" width="20" height="2" className={L} />
      <rect x="29" y="12" width="2.5" height="6" rx="1" className={L} />
      <rect x="29" y="24" width="2.5" height="8" rx="1" className={L} />
    </>
  ),
  stove: () => (
    <>
      <rect x="10" y="12" width="28" height="28" rx="3" className={S} />
      <circle cx="18" cy="19" r="3" className={L} />
      <circle cx="30" cy="19" r="3" className={L} />
      <rect x="14" y="26" width="20" height="11" rx="2" className={L} />
    </>
  ),
  microwave: () => (
    <>
      <rect x="6" y="16" width="36" height="18" rx="3" className={S} />
      <rect x="9" y="19" width="22" height="12" rx="2" className={L} />
      <rect x="34" y="20" width="4" height="3" rx="1" className={L} />
      <rect x="34" y="25" width="4" height="3" rx="1" className={L} />
    </>
  ),
  dishwasher: () => (
    <>
      <rect x="12" y="8" width="24" height="32" rx="3" className={S} />
      <rect x="15" y="11" width="18" height="4" rx="1" className={L} />
      <rect x="15" y="17" width="18" height="20" rx="2" className={L} />
    </>
  ),
  kitchenware: () => (
    <>
      <rect x="10" y="16" width="28" height="4" rx="2" className={L} />
      <rect x="22" y="12" width="4" height="4" rx="2" className={L} />
      <rect x="12" y="20" width="24" height="14" rx="3" className={S} />
      <rect x="8" y="24" width="4" height="3" rx="1.5" className={S} />
      <rect x="36" y="24" width="4" height="3" rx="1.5" className={S} />
    </>
  ),
  desk: () => (
    <>
      <rect x="6" y="18" width="36" height="5" rx="2" className={S} />
      <rect x="28" y="23" width="12" height="15" rx="2" className={S} />
      <rect x="31" y="26" width="6" height="3" rx="1" className={L} />
      <rect x="31" y="31" width="6" height="3" rx="1" className={L} />
      <rect x="9" y="23" width="3" height="15" className={L} />
    </>
  ),
  office_chair: () => (
    <>
      <rect x="18" y="8" width="14" height="14" rx="4" className={L} />
      <rect x="16" y="22" width="18" height="5" rx="2" className={S} />
      <rect x="24" y="27" width="2" height="7" className={S} />
      <rect x="14" y="35" width="22" height="3" rx="1.5" className={S} />
      <circle cx="15" cy="39" r="2" className={L} />
      <circle cx="25" cy="39" r="2" className={L} />
      <circle cx="35" cy="39" r="2" className={L} />
    </>
  ),
  monitor: () => (
    <>
      <rect x="8" y="10" width="32" height="20" rx="3" className={S} />
      <rect x="11" y="13" width="26" height="14" rx="2" className={L} />
      <rect x="22" y="30" width="4" height="5" className={S} />
      <rect x="16" y="35" width="16" height="3" rx="1.5" className={S} />
    </>
  ),
  books: () => (
    <>
      <rect x="10" y="31" width="28" height="5" rx="1" className={S} />
      <rect x="12" y="26" width="24" height="5" rx="1" className={L} />
      <rect x="11" y="21" width="26" height="5" rx="1" className={S} />
      <rect x="27" y="9" width="5" height="13" rx="1" transform="rotate(13 29 16)" className={L} />
    </>
  ),
  washer: () => (
    <>
      <rect x="12" y="8" width="24" height="32" rx="3" className={S} />
      <rect x="15" y="11" width="18" height="3" rx="1" className={L} />
      <circle cx="24" cy="26" r="9" className={L} />
      <circle cx="24" cy="26" r="4.5" className={S} />
    </>
  ),
  boxes: () => (
    <>
      <rect x="8" y="22" width="16" height="16" rx="2" className={S} />
      <rect x="14" y="22" width="2" height="16" className={L} />
      <rect x="8" y="27" width="16" height="2" className={L} />
      <rect x="26" y="14" width="14" height="14" rx="2" className={S} />
      <rect x="32" y="14" width="2" height="14" className={L} />
      <rect x="26" y="19" width="14" height="2" className={L} />
    </>
  ),
  suitcase: () => (
    <>
      <rect x="20" y="8" width="8" height="6" rx="3" className={S} />
      <rect x="12" y="13" width="24" height="27" rx="4" className={S} />
      <rect x="20" y="13" width="8" height="27" className={L} />
    </>
  ),
  tools: () => (
    <>
      <rect x="18" y="15" width="12" height="3" rx="1" className={S} />
      <rect x="20" y="16" width="8" height="4" className={L} />
      <rect x="8" y="20" width="32" height="16" rx="2" className={S} />
      <rect x="21" y="24" width="6" height="3" rx="1" className={L} />
    </>
  ),
  bicycle: () => (
    <g fill="none" className="stroke-petrol" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round">
      <circle cx="14" cy="31" r="7" />
      <circle cx="34" cy="31" r="7" />
      <path d="M14 31 L23 18 H31 L34 31 M23 18 L27 31 H14" />
      <path d="M31 18 h4" />
    </g>
  ),
  misc: () => (
    <>
      <rect x="8" y="8" width="32" height="32" rx="9" className={S} />
      <circle cx="16" cy="24" r="3" className={L} />
      <circle cx="24" cy="24" r="3" className={L} />
      <circle cx="32" cy="24" r="3" className={L} />
    </>
  ),

  // --- Added set ---
  ottoman: () => (
    <>
      <rect x="10" y="23" width="28" height="11" rx="5.5" className={L} />
      <rect x="11" y="31" width="26" height="6" rx="3" className={S} />
      <rect x="14" y="37" width="3" height="3" className={S} />
      <rect x="31" y="37" width="3" height="3" className={S} />
    </>
  ),
  speaker: () => (
    <>
      <rect x="15" y="8" width="18" height="32" rx="3" className={S} />
      <circle cx="24" cy="16" r="3.5" className={L} />
      <circle cx="24" cy="29" r="6" className={L} />
      <circle cx="24" cy="29" r="2.5" className={S} />
    </>
  ),
  crib: () => (
    <>
      <rect x="8" y="14" width="32" height="22" rx="3" className={S} />
      <rect x="12" y="18" width="24" height="14" rx="2" className={L} />
      <rect x="16" y="14" width="2" height="22" className={S} />
      <rect x="23" y="14" width="2" height="22" className={S} />
      <rect x="30" y="14" width="2" height="22" className={S} />
      <rect x="10" y="36" width="3" height="4" className={S} />
      <rect x="35" y="36" width="3" height="4" className={S} />
    </>
  ),
  vanity: () => (
    <>
      <circle cx="24" cy="14" r="7" className={S} />
      <circle cx="24" cy="14" r="4.5" className={L} />
      <rect x="10" y="24" width="28" height="5" rx="2" className={S} />
      <rect x="12" y="29" width="4" height="10" className={L} />
      <rect x="32" y="29" width="4" height="10" className={L} />
    </>
  ),
  kettle: () => (
    <>
      <path d="M15 23 q0 -2 2 -2 h14 q2 0 2 2 l-1.5 11 q-0.3 2 -2.3 2 h-10.4 q-2 0 -2.3 -2 z" className={S} />
      <rect x="20" y="15" width="9" height="6" rx="2" className={L} />
      <path d="M33 25 l6 -2 v3 z" className={S} />
      <rect x="21" y="26" width="7" height="6" rx="1.5" className={L} />
    </>
  ),
  coffee_machine: () => (
    <>
      <rect x="12" y="8" width="24" height="19" rx="3" className={S} />
      <rect x="15" y="11" width="11" height="6" rx="1.5" className={L} />
      <rect x="22" y="27" width="4" height="3" className={L} />
      <rect x="13" y="30" width="22" height="6" rx="2" className={S} />
      <rect x="19" y="32" width="10" height="6" rx="2" className={L} />
    </>
  ),
  trash_can: () => (
    <>
      <rect x="12" y="14" width="24" height="4" rx="2" className={S} />
      <rect x="20" y="10" width="8" height="4" rx="1.5" className={S} />
      <path d="M14 18 h20 l-1.6 20 q-0.2 2 -2 2 h-12.8 q-1.8 0 -2 -2 z" className={S} />
      <rect x="19" y="22" width="2" height="13" className={L} />
      <rect x="23" y="22" width="2" height="13" className={L} />
      <rect x="27" y="22" width="2" height="13" className={L} />
    </>
  ),
  bathtub: () => (
    <>
      <path d="M7 22 h34 v5 q0 7 -7 7 h-20 q-7 0 -7 -7 z" className={S} />
      <rect x="11" y="25" width="26" height="5" rx="2.5" className={L} />
      <rect x="33" y="14" width="2" height="8" className={S} />
      <circle cx="34" cy="13" r="2.5" className={S} />
      <rect x="12" y="34" width="2.5" height="4" className={S} />
      <rect x="33.5" y="34" width="2.5" height="4" className={S} />
    </>
  ),
  toilet: () => (
    <>
      <rect x="16" y="9" width="16" height="11" rx="2" className={S} />
      <ellipse cx="24" cy="27" rx="11" ry="8" className={S} />
      <ellipse cx="24" cy="26" rx="6.5" ry="4.5" className={L} />
      <rect x="15" y="33" width="18" height="6" rx="2" className={S} />
    </>
  ),
  hamper: () => (
    <>
      <rect x="12" y="13" width="24" height="4" rx="2" className={S} />
      <path d="M14 17 h20 l-1.8 21 q-0.2 2 -2 2 h-12.4 q-1.8 0 -2 -2 z" className={S} />
      <rect x="16.5" y="21" width="15" height="2" rx="1" className={L} />
      <rect x="16.5" y="27" width="15" height="2" rx="1" className={L} />
      <rect x="16.5" y="33" width="15" height="2" rx="1" className={L} />
    </>
  ),
  dryer: () => (
    <>
      <rect x="12" y="8" width="24" height="32" rx="3" className={S} />
      <rect x="15" y="11" width="11" height="3" rx="1" className={L} />
      <circle cx="30" cy="12.5" r="1.6" className={L} />
      <circle cx="24" cy="26" r="9" className={L} />
      <circle cx="20.5" cy="24.5" r="1.5" className={S} />
      <circle cx="27.5" cy="24.5" r="1.5" className={S} />
      <circle cx="24" cy="29.5" r="1.5" className={S} />
    </>
  ),
  printer: () => (
    <>
      <rect x="16" y="11" width="16" height="8" className={L} />
      <rect x="12" y="19" width="24" height="13" rx="2" className={S} />
      <circle cx="31" cy="23" r="1.5" className={L} />
      <rect x="16" y="29" width="16" height="9" rx="1" className={L} />
      <rect x="18" y="32" width="12" height="2" className={S} />
    </>
  ),
  vacuum: () => (
    <>
      <ellipse cx="17" cy="31" rx="9" ry="7" className={S} />
      <circle cx="17" cy="31" r="3" className={L} />
      <path d="M24 28 q9 -2 9 -13 l-4 0 q0 9 -7 10 z" className={L} />
      <rect x="28" y="11" width="5" height="6" rx="2.5" className={S} />
    </>
  ),
  ladder: () => (
    <>
      <rect x="14" y="8" width="3" height="32" rx="1.5" className={S} />
      <rect x="31" y="8" width="3" height="32" rx="1.5" className={S} />
      <rect x="14" y="13" width="20" height="3" className={L} />
      <rect x="14" y="20" width="20" height="3" className={L} />
      <rect x="14" y="27" width="20" height="3" className={L} />
      <rect x="14" y="34" width="20" height="3" className={L} />
    </>
  ),
  fan: () => (
    <>
      <circle cx="24" cy="19" r="12" className={S} />
      <ellipse cx="24" cy="12" rx="3.5" ry="6" className={L} />
      <ellipse cx="24" cy="12" rx="3.5" ry="6" className={L} transform="rotate(120 24 19)" />
      <ellipse cx="24" cy="12" rx="3.5" ry="6" className={L} transform="rotate(240 24 19)" />
      <circle cx="24" cy="19" r="2.5" className={L} />
      <rect x="22" y="31" width="4" height="6" className={S} />
      <rect x="18" y="37" width="12" height="3" rx="1.5" className={S} />
    </>
  ),

  // --- Appliances ---
  oven: () => (
    <>
      <rect x="12" y="8" width="24" height="32" rx="3" className={S} />
      <rect x="15" y="11" width="18" height="4" rx="1.5" className={L} />
      <circle cx="18" cy="13" r="1.3" className={S} />
      <circle cx="22" cy="13" r="1.3" className={S} />
      <rect x="15" y="18" width="18" height="19" rx="2" className={L} />
      <rect x="18" y="21" width="12" height="11" rx="1.5" className={S} />
    </>
  ),
  air_conditioner: () => (
    <>
      <rect x="9" y="15" width="30" height="12" rx="3" className={S} />
      <rect x="11" y="18" width="26" height="3.5" rx="1.75" className={L} />
      <rect x="14" y="30" width="2.5" height="7" rx="1.25" className={L} />
      <rect x="23" y="30" width="2.5" height="7" rx="1.25" className={L} />
      <rect x="32" y="30" width="2.5" height="7" rx="1.25" className={L} />
    </>
  ),
  water_heater: () => (
    <>
      <rect x="22" y="5" width="4" height="6" className={S} />
      <rect x="16" y="10" width="16" height="29" rx="8" className={S} />
      <rect x="20" y="15" width="8" height="6" rx="2" className={L} />
      <circle cx="24" cy="29" r="3" className={L} />
    </>
  ),
  freezer: () => (
    <>
      <rect x="9" y="19" width="30" height="17" rx="3" className={S} />
      <rect x="9" y="19" width="30" height="7" rx="3" className={L} />
      <rect x="29" y="21" width="6" height="2.5" rx="1.25" className={S} />
      <rect x="12" y="36" width="3" height="3" className={S} />
      <rect x="33" y="36" width="3" height="3" className={S} />
    </>
  ),
  heater: () => (
    <>
      <rect x="12" y="14" width="24" height="20" rx="3" className={S} />
      <rect x="16" y="16" width="2.5" height="16" rx="1.25" className={L} />
      <rect x="22" y="16" width="2.5" height="16" rx="1.25" className={L} />
      <rect x="28" y="16" width="2.5" height="16" rx="1.25" className={L} />
      <rect x="14" y="34" width="3" height="4" className={S} />
      <rect x="31" y="34" width="3" height="4" className={S} />
    </>
  ),

  // --- Special / exotic ---
  piano: () => (
    <>
      <rect x="10" y="9" width="28" height="21" rx="2" className={S} />
      <rect x="13" y="12" width="22" height="9" rx="1.5" className={L} />
      <rect x="10" y="24" width="28" height="6" className={L} />
      <rect x="14" y="24" width="1.8" height="4" className={S} />
      <rect x="18" y="24" width="1.8" height="4" className={S} />
      <rect x="24" y="24" width="1.8" height="4" className={S} />
      <rect x="28" y="24" width="1.8" height="4" className={S} />
      <rect x="32" y="24" width="1.8" height="4" className={S} />
      <rect x="13" y="30" width="3" height="8" className={S} />
      <rect x="32" y="30" width="3" height="8" className={S} />
    </>
  ),
  guitar: () => (
    <>
      <ellipse cx="22" cy="31" rx="10" ry="8" className={S} />
      <circle cx="22" cy="31" r="3" className={L} />
      <rect x="20" y="9" width="4" height="20" rx="1.5" className={S} />
      <rect x="18.5" y="6" width="7" height="5" rx="1.5" className={L} />
    </>
  ),
  aquarium: () => (
    <>
      <rect x="9" y="13" width="30" height="22" rx="2" className={S} />
      <rect x="12" y="19" width="24" height="13" rx="1" className={L} />
      <path d="M20 25 l5 -3 v6 z" className={S} />
      <circle cx="29" cy="23" r="1" className={S} />
      <circle cx="31" cy="26" r="0.8" className={S} />
      <rect x="11" y="35" width="26" height="3" className={S} />
    </>
  ),
  safe: () => (
    <>
      <rect x="11" y="10" width="26" height="28" rx="3" className={S} />
      <rect x="15" y="14" width="18" height="20" rx="2" className={L} />
      <circle cx="26" cy="24" r="4" className={S} />
      <circle cx="26" cy="24" r="1.5" className={L} />
      <rect x="17" y="22" width="3.5" height="4" rx="1.75" className={S} />
    </>
  ),
  grill: () => (
    <>
      <path d="M12 18 h24 l-2.5 9 q-0.5 2 -2.5 2 h-14 q-2 0 -2.5 -2 z" className={S} />
      <rect x="14" y="15" width="20" height="3.5" rx="1.75" className={L} />
      <circle cx="20" cy="22" r="1.2" className={L} />
      <circle cx="24" cy="22" r="1.2" className={L} />
      <circle cx="28" cy="22" r="1.2" className={L} />
      <rect x="16" y="29" width="3" height="9" className={S} />
      <rect x="29" y="29" width="3" height="9" className={S} />
    </>
  ),
  treadmill: () => (
    <>
      <rect x="8" y="29" width="26" height="7" rx="3.5" className={S} />
      <rect x="11" y="31" width="20" height="3" rx="1.5" className={L} />
      <rect x="30" y="13" width="3" height="17" rx="1.5" className={S} />
      <rect x="21" y="11" width="14" height="4" rx="2" className={S} />
      <rect x="10" y="36" width="3" height="3" className={S} />
      <rect x="29" y="36" width="3" height="3" className={S} />
    </>
  ),
  artwork: () => (
    <>
      <rect x="11" y="10" width="26" height="28" rx="2" className={S} />
      <rect x="15" y="14" width="18" height="20" rx="1" className={L} />
      <circle cx="28" cy="19" r="2.5" className={S} />
      <path d="M15 31 l5 -8 l4 5 l4 -6 l5 9 z" className={S} />
    </>
  ),
  bar_stool: () => (
    <>
      <ellipse cx="24" cy="13" rx="11" ry="4" className={S} />
      <ellipse cx="24" cy="12" rx="7" ry="2.5" className={L} />
      <rect x="15" y="14" width="3" height="24" rx="1.5" className={S} />
      <rect x="30" y="14" width="3" height="24" rx="1.5" className={S} />
      <rect x="15" y="30" width="18" height="3" rx="1.5" className={L} />
    </>
  ),

  toaster: () => (
    <>
      <rect x="10" y="20" width="26" height="18" rx="5" className={S} />
      <rect x="15" y="16" width="6" height="4" rx="2" className={L} />
      <rect x="25" y="16" width="6" height="4" rx="2" className={L} />
      <circle cx="40" cy="29" r="3" className={S} />
    </>
  ),
  blender: () => (
    <>
      <path d="M16 10 L32 10 L29 30 L19 30 Z" className={S} />
      <rect x="21" y="14" width="6" height="12" rx="2" className={L} />
      <rect x="14" y="30" width="20" height="8" rx="3" className={S} />
    </>
  ),
  stand_mixer: () => (
    <>
      <rect x="9" y="34" width="20" height="6" rx="3" className={S} />
      <rect x="11" y="12" width="6" height="24" rx="2" className={S} />
      <rect x="14" y="10" width="24" height="9" rx="4" className={S} />
      <path d="M22 28 a8 6 0 0 0 16 0 Z" className={L} />
    </>
  ),
  rice_cooker: () => (
    <>
      <rect x="10" y="22" width="28" height="16" rx="5" className={S} />
      <path d="M14 22 a10 8 0 0 1 20 0 Z" className={S} />
      <rect x="16" y="28" width="16" height="6" rx="3" className={L} />
      <circle cx="24" cy="13" r="2.5" className={S} />
    </>
  ),
  pots: () => (
    <>
      <rect x="13" y="20" width="22" height="18" rx="5" className={S} />
      <rect x="8" y="24" width="6" height="4" rx="2" className={S} />
      <rect x="34" y="24" width="6" height="4" rx="2" className={S} />
      <rect x="13" y="17" width="22" height="5" rx="2.5" className={L} />
      <circle cx="24" cy="15" r="2.5" className={S} />
    </>
  ),
  cutting_board: () => (
    <>
      <rect x="9" y="28" width="30" height="10" rx="5" className={L} />
      <circle cx="34" cy="33" r="2" className={S} />
      <path d="M12 16 L22 24 L19 27 L9 19 Z" className={S} />
      <rect x="20" y="23" width="9" height="4" rx="2" transform="rotate(38 20 23)" className={S} />
    </>
  ),
  bunk_bed: () => (
    <>
      <rect x="9" y="10" width="3" height="30" rx="1.5" className={S} />
      <rect x="9" y="19" width="26" height="4" rx="1.5" className={S} />
      <rect x="9" y="34" width="26" height="4" rx="1.5" className={S} />
      <rect x="13" y="14" width="20" height="5" rx="2" className={L} />
      <rect x="13" y="29" width="20" height="5" rx="2" className={L} />
      <rect x="35" y="10" width="3" height="30" rx="1.5" className={S} />
    </>
  ),
  shoe_rack: () => (
    <>
      <rect x="11" y="10" width="3" height="30" rx="1.5" className={S} />
      <rect x="34" y="10" width="3" height="30" rx="1.5" className={S} />
      <rect x="13" y="15" width="22" height="3" rx="1.5" className={L} />
      <rect x="13" y="24" width="22" height="3" rx="1.5" className={L} />
      <path d="M15 35 h7 q3 0 3 2 h-10 z" className={S} />
      <path d="M26 35 h7 q3 0 3 2 h-10 z" className={S} />
    </>
  ),
  bench: () => (
    <>
      <rect x="8" y="24" width="32" height="6" rx="2" className={S} />
      <rect x="10" y="20" width="28" height="5" rx="2.5" className={L} />
      <rect x="12" y="30" width="4" height="9" className={S} />
      <rect x="32" y="30" width="4" height="9" className={S} />
    </>
  ),
  sink: () => (
    <>
      <path d="M10 22 h28 v3 q0 9 -9 9 h-10 q-9 0 -9 -9 z" className={S} />
      <ellipse cx="24" cy="25" rx="9" ry="4.5" className={L} />
      <rect x="23" y="11" width="2.5" height="8" className={S} />
      <path d="M19 13 q5 -4 10 0 h-2.5 q-2.5 -2 -5 0 z" className={S} />
    </>
  ),
  towel_rack: () => (
    <>
      <rect x="9" y="14" width="30" height="3" rx="1.5" className={S} />
      <rect x="10" y="13" width="2.5" height="5" rx="1" className={S} />
      <rect x="35.5" y="13" width="2.5" height="5" rx="1" className={S} />
      <path d="M16 16 h16 v18 q0 2 -2 2 h-12 q-2 0 -2 -2 z" className={L} />
    </>
  ),
  chandelier: () => (
    <>
      <rect x="22" y="6" width="4" height="6" rx="1" className={S} />
      <rect x="11" y="12" width="26" height="3.5" rx="1.75" className={S} />
      <rect x="13.5" y="15" width="2.5" height="7" className={S} />
      <rect x="22.75" y="15" width="2.5" height="7" className={S} />
      <ellipse cx="14.75" cy="26" rx="5" ry="6" className={L} />
      <ellipse cx="24" cy="26" rx="5" ry="6" className={L} />
      <ellipse cx="33.25" cy="26" rx="5" ry="6" className={L} />
    </>
  ),
  wall_clock: () => (
    <>
      <circle cx="24" cy="24" r="16" className={S} />
      <circle cx="24" cy="24" r="12" className={L} />
      <rect x="22.75" y="14" width="2.5" height="11" rx="1.25" className={S} />
      <rect x="24" y="22.75" width="9" height="2.5" rx="1.25" className={S} />
      <circle cx="24" cy="24" r="2" className={S} />
    </>
  ),
  curtains: () => (
    <>
      <rect x="8" y="9" width="32" height="3.5" rx="1.75" className={S} />
      <path d="M10 13 q5 3 5 13 q0 8 -5 12 q4 1 8 0 q-3 -10 -1 -25 z" className={L} />
      <path d="M38 13 q-5 3 -5 13 q0 8 5 12 q-4 1 -8 0 q3 -10 1 -25 z" className={L} />
    </>
  ),
  router: () => (
    <>
      <rect x="20" y="10" width="3" height="16" rx="1.5" transform="rotate(20 21.5 18)" className={S} />
      <rect x="27" y="10" width="3" height="16" rx="1.5" transform="rotate(-20 28.5 18)" className={S} />
      <rect x="8" y="26" width="32" height="14" rx="3" className={S} />
      <rect x="12" y="30" width="24" height="6" rx="3" className={L} />
    </>
  ),
  filing_cabinet: () => (
    <>
      <rect x="13" y="7" width="22" height="34" rx="2.5" className={S} />
      <rect x="16" y="10" width="16" height="8" rx="1.5" className={L} />
      <rect x="16" y="20" width="16" height="8" rx="1.5" className={L} />
      <rect x="16" y="30" width="16" height="8" rx="1.5" className={L} />
      <rect x="22" y="13" width="4" height="2" rx="1" className={S} />
    </>
  ),
  desk_lamp: () => (
    <>
      <rect x="14" y="38" width="20" height="4" rx="2" className={S} />
      <rect x="22.5" y="20" width="3" height="20" rx="1.5" className={S} />
      <rect x="24" y="14" width="12" height="3" rx="1.5" transform="rotate(40 30 15.5)" className={S} />
      <path d="M30 8 l9 5 -6 9 z" className={L} />
    </>
  ),
  stroller: () => (
    <>
      <path d="M11 13 L33 13 A11 11 0 0 1 11 13 Z" className={L} />
      <rect x="11" y="11" width="24" height="3" rx="1.5" className={S} />
      <path d="M33 12 L43 8 L44 11 L34 15 Z" className={S} />
      <rect x="13" y="14" width="3" height="20" rx="1.5" className={S} transform="rotate(18 14.5 24)" />
      <rect x="31" y="14" width="3" height="20" rx="1.5" className={S} transform="rotate(-18 32.5 24)" />
      <circle cx="15" cy="38" r="4" className={S} />
      <circle cx="33" cy="38" r="4" className={S} />
    </>
  ),
  high_chair: () => (
    <>
      <rect x="18" y="9" width="12" height="13" rx="3" className={S} />
      <rect x="20" y="13" width="8" height="7" rx="2" className={L} />
      <rect x="16" y="20" width="16" height="3" rx="1.5" className={S} />
      <rect x="19" y="22" width="3" height="16" className={S} transform="rotate(8 20.5 30)" />
      <rect x="26" y="22" width="3" height="16" className={S} transform="rotate(-8 27.5 30)" />
      <rect x="18" y="30" width="12" height="2.5" rx="1.25" className={S} />
    </>
  ),
  toy_box: () => (
    <>
      <rect x="11" y="22" width="26" height="16" rx="3" className={S} />
      <rect x="14" y="24" width="20" height="9" rx="2" className={L} />
      <rect x="15" y="15" width="8" height="8" rx="1.5" className={S} />
      <rect x="25" y="13" width="8" height="10" rx="1.5" className={L} />
      <circle cx="30" cy="17" r="3.5" className={S} />
    </>
  ),
  scooter: () => (
    <>
      <rect x="13" y="32" width="20" height="3.5" rx="1.75" className={S} />
      <rect x="31" y="12" width="3.5" height="22" rx="1.75" className={S} />
      <rect x="28" y="11" width="12" height="3" rx="1.5" className={S} />
      <circle cx="15" cy="38" r="4" className={S} />
      <circle cx="34" cy="38" r="4" className={S} />
    </>
  ),
  pet_crate: () => (
    <>
      <rect x="10" y="14" width="28" height="22" rx="5" className={S} />
      <rect x="14" y="18" width="20" height="16" rx="3" className={L} />
      <rect x="18" y="18" width="2.5" height="16" className={S} />
      <rect x="23" y="18" width="2.5" height="16" className={S} />
      <rect x="28" y="18" width="2.5" height="16" className={S} />
      <rect x="19" y="9" width="10" height="4" rx="2" className={S} />
    </>
  ),
  cat_tree: () => (
    <>
      <rect x="22" y="10" width="4" height="28" rx="1.5" className={S} />
      <ellipse cx="24" cy="14" rx="11" ry="4" className={L} />
      <ellipse cx="24" cy="27" rx="9" ry="3.5" className={L} />
      <circle cx="33" cy="34" r="3" className={S} />
      <rect x="32.5" y="29" width="1.5" height="5" className={S} />
    </>
  ),
  patio_umbrella: () => (
    <>
      <path d="M8 20 Q24 6 40 20 Q32 16 24 20 Q16 16 8 20 Z" className={L} />
      <rect x="23" y="20" width="2" height="18" className={S} />
      <rect x="18" y="37" width="12" height="3" rx="1.5" className={S} />
    </>
  ),
  lawnmower: () => (
    <>
      <rect x="8" y="22" width="22" height="9" rx="2" className={S} />
      <path d="M28 28 L40 10 L43 12 L31 30 Z" className={S} />
      <circle cx="13" cy="34" r="5" className={S} />
      <circle cx="26" cy="34" r="5" className={S} />
    </>
  ),
  cooler: () => (
    <>
      <rect x="9" y="18" width="30" height="20" rx="3" className={S} />
      <rect x="9" y="18" width="30" height="6" rx="3" className={L} />
      <rect x="20" y="12" width="8" height="4" rx="2" className={S} />
    </>
  ),
  wheelbarrow: () => (
    <>
      <path d="M10 16 H38 L31 28 H15 Z" className={L} />
      <rect x="6" y="27" width="30" height="3" rx="1.5" className={S} transform="rotate(8 21 28)" />
      <circle cx="14" cy="34" r="5" className={S} />
      <rect x="30" y="30" width="3" height="8" rx="1.5" className={S} />
    </>
  ),
  surfboard: () => (
    <>
      <rect x="20" y="6" width="8" height="34" rx="4" className={S} />
      <rect x="23" y="10" width="2" height="26" rx="1" className={L} />
      <rect x="22" y="38" width="4" height="5" rx="1" className={S} />
    </>
  ),
  skis: () => (
    <>
      <path d="M17 8 Q22 8 21 14 L19 40 H15 L13 14 Q12 8 17 8 Z" className={L} />
      <path d="M27 8 Q32 8 31 14 L29 40 H25 L23 14 Q22 8 27 8 Z" className={L} />
      <rect x="35" y="10" width="2" height="30" rx="1" className={S} />
      <rect x="32" y="10" width="8" height="3" rx="1.5" className={S} />
    </>
  ),
  dumbbells: () => (
    <>
      <rect x="14" y="22" width="20" height="4" rx="2" className={S} />
      <rect x="8" y="15" width="7" height="18" rx="2" className={S} />
      <rect x="33" y="15" width="7" height="18" rx="2" className={S} />
      <rect x="20" y="21" width="8" height="6" rx="3" className={L} />
    </>
  ),
};
